import { useEffect, useState, useCallback } from 'react';
import { X, Plus, Send } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import StellungnahmeEditor from './StellungnahmeEditor';
import StellungnahmeUebersicht from './StellungnahmeUebersicht';

type Akte = Record<string, any>;

const TABS = [
  { id: 'uebersicht', label: 'Übersicht' },
  { id: 'kunden', label: 'Für Kunden' },
  { id: 'uns', label: 'Für Uns' },
  { id: 'versand', label: 'Versand' },
] as const;
type TabId = (typeof TABS)[number]['id'];

const EUR = (v: any) =>
  v == null || v === '' ? '–' : `${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

// Modulebene (stabile Identität) -> Inputs behalten beim Tippen den Fokus.
function Feld({ label, children }: { label: string; children: any }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-500">{label}</span>
      {children}
    </label>
  );
}

interface Props {
  akte: Akte;
  onChanged?: () => void;
}

export default function StellungnahmeTab({ akte, onChanged }: Props) {
  const [tab, setTab] = useState<TabId>('uebersicht');
  const [editSn, setEditSn] = useState<any | null>(null);

  const [status, setStatus] = useState<string>(akte?.stellungnahme_status ?? '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const [stellungnahmen, setStellungnahmen] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Generator-Formular
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formArt, setFormArt] = useState<'kunde' | 'honorar'>('kunde');
  const [formBetreff, setFormBetreff] = useState('');
  const [formKanzlei, setFormKanzlei] = useState('');
  const [formAz, setFormAz] = useState('');
  const [formFrist, setFormFrist] = useState('');
  const [formEinleitung, setFormEinleitung] = useState('');

  useEffect(() => {
    setStatus(akte?.stellungnahme_status ?? '');
  }, [akte?.id]);

  const load = useCallback(async () => {
    if (!akte?.id) return;
    setLoading(true);
    const { data } = await supabase
      .from('stellungnahmen')
      .select('*, stellungnahme_positionen(*)')
      .eq('akte_id', akte.id)
      .order('erstellt_am', { ascending: false });
    setStellungnahmen(data ?? []);
    setLoading(false);
  }, [akte?.id]);

  useEffect(() => {
    let aktiv = true;
    (async () => {
      await load();
      if (!aktiv) return;
    })();
    return () => {
      aktiv = false;
    };
  }, [load]);

  const save = useCallback(
    async (patch: Record<string, any>) => {
      if (!akte?.id) return;
      setSaving(true);
      setMsg(null);
      const { error } = await supabase.from('akten').update(patch).eq('id', akte.id);
      setSaving(false);
      if (error) {
        setMsg('Fehler beim Speichern: ' + error.message);
        return;
      }
      setMsg('Gespeichert.');
      onChanged?.();
      setTimeout(() => setMsg(null), 2000);
    },
    [akte?.id, onChanged],
  );

  const setStatusOf = useCallback(
    async (id: string, neu: string) => {
      const patch: Record<string, any> = { status: neu };
      if (neu === 'versendet') patch.versendet_am = new Date().toISOString();
      await supabase.from('stellungnahmen').update(patch).eq('id', id);
      setMsg(neu === 'freigegeben' ? 'Freigegeben.' : neu === 'versendet' ? 'Als versendet markiert.' : 'Aktualisiert.');
      setTimeout(() => setMsg(null), 2000);
      await load();
    },
    [load],
  );

  function mailtoFor(s: any): string {
    const to = akte?.anwalt_email || akte?.kunde_email || akte?.versicherung_email || '';
    const subj = encodeURIComponent(s.betreff || 'Stellungnahme');
    const body = encodeURIComponent(
      'Sehr geehrte Damen und Herren,\n\nanbei übersenden wir unsere Stellungnahme (siehe Anhang).\n\nMit freundlichen Grüßen\nKfz Sachverständigenbüro Koop\nAndreas Koop',
    );
    return `mailto:${to}?subject=${subj}&body=${body}`;
  }

  function openForm(art: 'kunde' | 'honorar') {
    setFormArt(art);
    setFormBetreff(
      art === 'honorar'
        ? `Honorar-Stellungnahme ${akte?.akten_nr || ''}`.trim()
        : `Stellungnahme zu Haftpflichtgutachten ${akte?.akten_nr || ''}`.trim(),
    );
    setFormKanzlei('');
    setFormAz('');
    setFormFrist(akte?.schriftsatzfrist ? String(akte.schriftsatzfrist).slice(0, 10) : '');
    setFormEinleitung('');
    setShowForm(true);
  }

  const createStellungnahme = useCallback(async () => {
    if (!akte?.id) return;
    if (!formBetreff.trim()) {
      setMsg('Bitte einen Betreff angeben.');
      return;
    }
    setCreating(true);
    setMsg(null);
    const { error } = await supabase.from('stellungnahmen').insert({
      akte_id: akte.id,
      art: formArt,
      status: 'entwurf_erstellt',
      betreff: formBetreff.trim(),
      empfaenger_kanzlei: formKanzlei.trim() || null,
      aktenzeichen_ag: formAz.trim() || null,
      schriftsatzfrist: formFrist || null,
      einleitung: formEinleitung.trim() || null,
      version: 1,
      erstellt_am: new Date().toISOString(),
    });
    setCreating(false);
    if (error) {
      setMsg('Fehler beim Anlegen: ' + error.message);
      return;
    }
    if (!status) {
      setStatus('entwurf_erstellt');
      save({ stellungnahme_status: 'entwurf_erstellt' });
    }
    setShowForm(false);
    setMsg('Stellungnahme angelegt.');
    setTimeout(() => setMsg(null), 2000);
    await load();
  }, [akte?.id, formArt, formBetreff, formKanzlei, formAz, formFrist, formEinleitung, status, save, load]);

  const kundenListe = stellungnahmen.filter((s) => s.art !== 'honorar');
  const honorarListe = stellungnahmen.filter((s) => s.art === 'honorar');
  const versandListe = stellungnahmen.filter((s) => ['versendet', 'freigegeben', 'erledigt'].includes(s.status));

  // ---- gemeinsame Bausteine -------------------------------------------------

  function renderForm() {
    if (!showForm) return null;
    return (
      <div className="mb-4 border border-gray-200 rounded-xl p-4 bg-gray-50/60">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-gray-700">
            Neue {formArt === 'honorar' ? 'Honorar' : 'Kunden'}-Stellungnahme anlegen
          </span>
          <button type="button" onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Feld label="Schriftsatzfrist">
            <input
              type="date"
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
              value={formFrist}
              onChange={(e) => setFormFrist(e.target.value)}
            />
          </Feld>
          <Feld label={formArt === 'honorar' ? 'Empfänger (Versicherung)' : 'Empfänger / Kanzlei'}>
            <input
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
              value={formKanzlei}
              onChange={(e) => setFormKanzlei(e.target.value)}
              placeholder={formArt === 'honorar' ? 'z.B. R+V Allgemeine Versicherung' : 'z.B. Rechtsanwalt Sturm'}
            />
          </Feld>
          <div className="sm:col-span-2">
            <Feld label="Betreff">
              <input
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
                value={formBetreff}
                onChange={(e) => setFormBetreff(e.target.value)}
              />
            </Feld>
          </div>
          <Feld label="Aktenzeichen Gegenseite">
            <input
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
              value={formAz}
              onChange={(e) => setFormAz(e.target.value)}
              placeholder="z.B. 1234/26"
            />
          </Feld>
          <div className="sm:col-span-2">
            <Feld label="Einleitung / Notiz (optional)">
              <textarea
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white min-h-[70px]"
                value={formEinleitung}
                onChange={(e) => setFormEinleitung(e.target.value)}
                placeholder="Kurzer Einleitungstext oder interne Notiz …"
              />
            </Feld>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-3">
          <button
            type="button"
            onClick={() => setShowForm(false)}
            className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={createStellungnahme}
            disabled={creating}
            className="px-4 py-1.5 text-sm font-medium text-white bg-[#B91C1C] hover:bg-[#991B1B] rounded-lg disabled:opacity-50"
          >
            {creating ? 'Legt an…' : 'Anlegen'}
          </button>
        </div>
      </div>
    );
  }

  function renderListe(liste: any[], leerText: string) {
    if (loading) return <p className="text-sm text-gray-400">Lädt…</p>;
    if (liste.length === 0) return <p className="text-sm text-gray-500">{leerText}</p>;
    return (
      <ul className="divide-y divide-gray-100">
        {liste.map((s) => (
          <li key={s.id} className="py-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                {s.art === 'honorar' ? 'Honorar' : 'Kunde'}
              </span>
              <span className="text-sm font-medium text-gray-800">{s.betreff || 'Stellungnahme'}</span>
              <span className="text-xs text-gray-400 ml-auto">{s.status}</span>
              <button
                type="button"
                onClick={() => setEditSn(s)}
                className="text-xs font-medium text-[#B91C1C] hover:underline"
              >
                Bearbeiten
              </button>
              {s.status !== 'freigegeben' && s.status !== 'versendet' && (
                <button
                  type="button"
                  onClick={() => setStatusOf(s.id, 'freigegeben')}
                  className="text-xs font-medium text-green-700 hover:underline"
                >
                  Freigeben
                </button>
              )}
              <a href={mailtoFor(s)} className="text-xs font-medium text-[#1f3a5c] hover:underline">
                E-Mail
              </a>
              {s.status === 'freigegeben' && (
                <button
                  type="button"
                  onClick={() => setStatusOf(s.id, 'versendet')}
                  className="text-xs font-medium text-gray-600 hover:underline"
                >
                  Als versendet
                </button>
              )}
            </div>
            {s.empfaenger_kanzlei && (
              <p className="text-xs text-gray-500 mt-1">
                An: {s.empfaenger_kanzlei}
                {s.aktenzeichen_ag ? ` · Az: ${s.aktenzeichen_ag}` : ''}
              </p>
            )}
            {s.einleitung && <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">{s.einleitung}</p>}
            {Array.isArray(s.stellungnahme_positionen) && s.stellungnahme_positionen.length > 0 && (
              <ul className="mt-2 space-y-1">
                {s.stellungnahme_positionen
                  .slice()
                  .sort((a: any, b: any) => (a.reihenfolge ?? 0) - (b.reihenfolge ?? 0))
                  .map((p: any) => (
                    <li key={p.id} className="text-xs text-gray-600 flex gap-2">
                      <span className="font-medium text-gray-700">{p.bezeichnung}</span>
                      <span className="text-gray-400">Kürzung: {EUR(p.betrag_kuerzung)}</span>
                    </li>
                  ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    );
  }

  // ---- Tabs -----------------------------------------------------------------

  function renderUebersicht() {
    return <StellungnahmeUebersicht akte={akte} onChanged={onChanged} />;
  }

  function renderGeneratorTab(art: 'kunde' | 'honorar', liste: any[], titel: string, hinweis: string) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-800">{titel}</h3>
          {!showForm && (
            <button
              type="button"
              onClick={() => openForm(art)}
              className="flex items-center gap-1.5 text-xs font-medium text-white bg-[#B91C1C] hover:bg-[#991B1B] px-3 py-1.5 rounded-lg"
            >
              <Plus className="w-3.5 h-3.5" /> Neue Stellungnahme
            </button>
          )}
        </div>
        <p className="text-xs text-gray-400 mb-3">{hinweis}</p>
        {showForm && formArt === art && renderForm()}
        {renderListe(liste, 'Noch keine Stellungnahme angelegt. Lege mit „Neue Stellungnahme“ eine an.')}
      </div>
    );
  }

  function renderVersand() {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Send className="w-4 h-4 text-gray-500" /> Versand
        </h3>
        <p className="text-xs text-gray-400 mb-3">
          Freigegebene Stellungnahmen. „E-Mail" öffnet dein Mailprogramm mit Empfänger &amp; Betreff (PDF aus dem Editor anhängen);
          danach „Als versendet" markieren.
        </p>
        {renderListe(versandListe, 'Noch nichts zum Versand. Gib eine Stellungnahme über „Für Kunden“/„Für Uns“ frei.')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Tab-Leiste */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={
              'px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ' +
              (tab === t.id
                ? 'border-[#B91C1C] text-[#B91C1C]'
                : 'border-transparent text-gray-500 hover:text-gray-800')
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {saving && <span className="text-xs text-gray-400">speichert…</span>}
      {msg && <span className="text-xs text-gray-500">{msg}</span>}

      {tab === 'uebersicht' && renderUebersicht()}
      {tab === 'kunden' &&
        renderGeneratorTab(
          'kunde',
          kundenListe,
          'Anschreiben an Kunde / Anwalt',
          'Stellungnahme, wenn der Kunde oder sein Anwalt dich beauftragt (z.B. gekürzte Rep.-Kosten, Nachbesichtigung, Regulierungsproblem).',
        )}
      {tab === 'uns' &&
        renderGeneratorTab(
          'honorar',
          honorarListe,
          'In eigener Sache (Honorar)',
          'Stellungnahme/Widerspruch in eigener Sache, z.B. bei Honorarkürzung durch die Versicherung.',
        )}
      {tab === 'versand' && renderVersand()}

      {editSn && (
        <StellungnahmeEditor
          akte={akte}
          stellungnahme={editSn}
          onClose={() => setEditSn(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
